import {useState, useEffect, useCallback} from "preact/compat";
import {MapFormatter} from "../../../utils";

export const useCategoriesFormTree = props => {
    // Destructured props
    const {
        rawData,                 // "rawData" stands for all the available categories.
        selectedCategories,      // The state containing all the selected categories.
        shouldFilter,            // Flag to validate whether any operation is allowed to run.
        collapseController,
        handleCollapseController
    } = props;

    // State to store the current input's additional categories
    const [additionalCategories, setAdditionalCategories] = useState([]);

    // Handles the collapsed state of each group
    const handleCollapse = useCallback(props => {
        // Destructured props
        const {
            id = undefined,
            target = undefined,
            valuesArray = undefined
        } = props;

        // Creates a clone of the collapseController state to be directly modified.
        // It spreads the state to assign its content by value, not by reference.
        // When a value gets assigned by reference, React doesn't count that as a state update
        // (e.g.: let variable = state).
        let clone = [...collapseController];

        // This is a special case for when the user
        // explicitly clicks the collapse button of the group.
        // It's probably the most simple case that can be stumbled into in this feature.
        if (!!target && target.toUpperCase() === "CLICK") {
            // Retrieves the item index of the desired item
            let cloneItem = clone.findIndex(item => item.id === id);

            // Switches the state between collapsed and not collapsed
            clone[cloneItem].isCollapsed = !clone[cloneItem].isCollapsed;

            // Finally, assigns the new array into the collapseController state
            handleCollapseController({
                data: clone
            });

            return;
        }

        if (Array.isArray(valuesArray) && valuesArray.length > 0) {
            // An array containing the id of the groups that must not be collapsed
            let excludesList = [];

            // Iterates through each category
            for (let i = 0; i < rawData.length; i++) {
                if (!rawData[i].childs) {
                    continue;
                }

                // This block is responsible mostly for
                // segregating the group categories that shouldn't be collapsed.
                rawData[i].childs.forEach(child => {
                    if (child.id === valuesArray[0]) {
                        excludesList.push(child.parentId);

                        // Checks if the selected category has any additional categories bound to it
                        if (child["additionalCategories"].length > 0) {
                            // An array we use to temporarily store the ids of the additional category group id
                            let rawAdditional = [];

                            child["additionalCategories"].forEach(additional => {
                                // Appends the group id in which the additional category is stored in
                                // into the excludesList array.
                                // We're doing this because it shouldn't be collapsed.
                                excludesList.push(additional.id);

                                // Appends the group id of the additional to the rawAdditional array
                                rawAdditional.push(additional.id);

                                // Finally, sets the rawAdditional array as the additionalCategories state
                                setAdditionalCategories(rawAdditional);
                            })
                        } else {
                            // Resets the additionalCategories state
                            additionalCategories.length > 0 && setAdditionalCategories([]);
                        }
                    }
                })
            }

            // This loop is responsible for controlling the collapse change of each group
            for (let i = 0; i < clone.length; i++) {
                // Opens the groups that shouldn't be collapsed
                if (excludesList.includes(clone[i].id)) {
                    clone[i].isCollapsed = false;

                    continue;
                }

                // Closes the groups that should be collapsed
                clone[i].isCollapsed = true;
            }
        }

        // This block is responsible for cleaning all the collapses
        if (Array.isArray(valuesArray) && valuesArray.length === 0) {
            clone.forEach(item => {
                item.isCollapsed = false;
            })

            // Resets the additionalCategories state
            setAdditionalCategories([]);
        }

        // Finally, assigns the new array into the collapseController state
        handleCollapseController({
            data: clone
        });
    }, [
        additionalCategories,
        collapseController,
        rawData
    ]);

    // Effect to format the groups into the collapseController state
    useEffect(() => {
        // Prevents anything from happening if there aren't any data available
        if (!rawData || collapseController.length > 0) {
            return;
        }

        // The array in which the formatted values will be stored into
        let values = [];

        // Formats each individual value
        rawData.forEach(item => {
            values.push({
                id: item.id,
                isCollapsed: false
            })
        })

        handleCollapseController({
            data: values
        });
    }, [
        collapseController,
        rawData
    ]);

    // Calls the collapse handler every time a category change is made
    useEffect(() => {
        // If the filter isn't allowed to perform an operation, then it does nothing
        if (!shouldFilter) {
            return;
        }

        // Finally, calls the collapse method with the formatted values
        handleCollapse({
            valuesArray: MapFormatter(selectedCategories)
        });
    }, [selectedCategories, shouldFilter]);

    return {
        collapseController,
        additionalCategories,
        handleCollapse
    }
}
